import axios, { AxiosError, AxiosRequestHeaders } from "axios";
import { toast } from "sonner";
import { createHeaderToken, generateRandomBase64String } from "../helpers";
import { getCookie } from "cookies-next";
import config from "../config";

export const API_URL = process.env.NEXT_PUBLIC_API_URL as string;

const token = getCookie(config.ACCESS_TOKEN);

const globalHeaders: { [key: string]: string } = {
  "Content-type": "application/json",
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, PATCH, OPTIONS",
  "Access-Control-Allow-Headers":
    "Content-Type, Authorization, X-Brimble-Signature, Accept-Version, Content-Length, Content-MD5, Date, X-Api-Version",
};

globalHeaders["X-Brimble-Signature"] = createHeaderToken({}, 30);

if (token) {
  globalHeaders.Authorization = `Bearer ${token}`;
}

const axios_server = axios.create({
  baseURL: API_URL,
  timeout: 25000,
  headers: globalHeaders,
});

const payment_backend = axios.create({
  baseURL: config.PAYMENT_BASE_URL,
  timeout: 25000,
  headers: {
    "X-Payment-Key": generateRandomBase64String(16),
    ...globalHeaders,
  },
});

const handleRequestError = (
  error: AxiosError,
  type: "toast" | "return" = "toast",
) => {
  const { response, request, message } = error;

  if ((response as any)?.data?.error) {
    const errorMsg = (response as any)?.data?.error;
    return type === "return" ? errorMsg : toast.error(errorMsg);
  }

  if ((response as any)?.data?.message) {
    const errorMsg = (response as any)?.data?.message;
    return type === "return" ? errorMsg : toast.error(errorMsg);
  }

  // Handle response data with different structures
  if (response?.data) {
    const { data } = response as { data: any };

    if (data?.errors?.length) {
      const errorMsg = data?.errors[0]?.msg;
      if (type === "return") {
        return errorMsg;
      } else {
        data?.errors.forEach((error: any) => {
          toast.error(error.msg);
        });
        return;
      }
    } else if (data?.message) {
      const errorMsg = data?.message;
      return type === "return" ? errorMsg : toast.error(errorMsg);
    } else {
      // Handle HTTP status errors without specific message
      const statusText = response.statusText || `HTTP ${response.status}`;
      const errorMsg = `Request failed: ${statusText}`;
      return type === "return" ? errorMsg : toast.error(errorMsg);
    }
  } else if (request && message === "Network Error") {
    const errorMsg = "Please check your internet connection.";
    return type === "return"
      ? errorMsg
      : toast.error("Network error", {
          description: errorMsg,
        });
  } else {
    // Fallback for other errors
    const errorMsg = message || "Something went wrong";
    return type === "return"
      ? errorMsg
      : toast.error("Something went wrong", {
          description: message,
        });
  }
};

const getFaviconUrl = (domain: string) => {
  return `/api/get.favicon?domain=${domain?.split("https://")[1]}`;
};

const get = async (endpoint: string, headers?: AxiosRequestHeaders) => {
  const { data } = await axios_server.get(endpoint, headers ? { headers } : {});
  return data;
};

const post = async (
  endpoint: string,
  payload: any,
  headers?: AxiosRequestHeaders,
) => {
  const { data } = await axios_server.post(
    endpoint,
    payload,
    headers ? { headers } : {},
  );
  return data;
};

export {
  axios_server,
  payment_backend,
  handleRequestError,
  getFaviconUrl,
  get,
  post,
};
